import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';

import { useGetPeriodMetricsQuery } from '../../../../../services/PaymentToolApi';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { formatMoney } from '../../../../../utils';
import { IconButton, Tooltip } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'; 
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'; 

function getTransactionSummary(row, transactionTypeName) {
  if (!row[transactionTypeName]) return "-";

  const count = row[transactionTypeName + "Count"] || 0;
  const amount = formatMoney(row[transactionTypeName]);

  if (transactionTypeName === "penalty" || transactionTypeName === "deduction") {
    return `- ${amount} (${count})`;
  } else {
    return `${amount} (${count})`;
  }
}

export default function PaymentByPeriodsTable({ selectedYear, selectedPeriod, setSelectedUser }) {

  const { data, error, isLoading, isFetching } = useGetPeriodMetricsQuery({ year: selectedYear, period: selectedPeriod }, { skip: !selectedPeriod || !selectedYear });

  function onUserActionClick(row) {
    setSelectedUser({ id: row.userID, fullName: row.fullName })
  }

  const columns = [
    {
      field: 'fullName',
      headerName: 'USER',
      flex: 1.5,
    },
    {
      field: 'paymentsEmail',
      headerName: 'PAYMENT EMAIL',
      flex: 3,
      headerAlign: 'center',
      align: 'center',
      // valueGetter: (params) => params.row.paymentsEmail || '-',
      renderCell: (params) => {
        const email = params.row.paymentsEmail;
        if (!email) {
          return <span>-</span>; 
        }
        const paymentsEmailVerified = params.row.paymentsEmailVerified ? (
          <Tooltip title="Verified">
            <VerifiedOutlinedIcon style={{ marginLeft: '0.5em' }} color="success" />
          </Tooltip>
        ) : (
          <Tooltip title="Not Verified">
            <NewReleasesOutlinedIcon style={{ marginLeft: '0.5em' }} color="error" />
          </Tooltip>
        );
        return (
          <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <span style={{ textAlign: 'center', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</span>
            <span style={{ marginLeft: 'auto' }}>{paymentsEmailVerified}</span>
          </span>
        );
      },
    },
    {
      field: 'reward',
      headerName: 'REWARDS',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => getTransactionSummary(params.row, 'reward'),
    },
    {
      field: 'royalty',
      headerName: 'ROYALTIES',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => getTransactionSummary(params.row, 'royalty'),
    },
    {
      field: 'bonus',
      headerName: 'BONUSES',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => getTransactionSummary(params.row, 'bonus'),
    },
    {
      field: 'penalty',
      headerName: 'ERRORS',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <p style={{ color: 'red' }}>{getTransactionSummary(params.row, 'penalty')}</p>
    },
    {
      field: 'deduction',
      headerName: 'DEDUCTIONS',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <p style={{ color: 'orange' }}>{getTransactionSummary(params.row, 'deduction')}</p>
    },
    {
      field: 'transaction',
      headerName: 'TRANSACTIONS',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.totalCount || 0,
    },
    {
      field: 'initialBalance',
      headerName: 'INITIAL BALANCE',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.initialBalance ? formatMoney(params.row.initialBalance || "0") : "-",
    },
    {
      field: 'amount',
      headerName: 'TOTAL AMOUNT',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const formattedAmount = formatMoney(params.value || 0);
        return formattedAmount.includes('-') ? <p style={{ color: 'red' }}>{formattedAmount}</p> : formattedAmount;
      },
    },
    {
      field: 'buttons',
      headerName: '',
      width: 50,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <Tooltip title="Go to User" placement="top-start">
        <IconButton color="primary" onClick={() => onUserActionClick(params.row)}>
          <OpenInNewOutlinedIcon />
        </IconButton>
      </Tooltip>,
    },
  ];

  function deleteDuplicatedRows(rows){
    let uniqueRows = [];
    let uniqueRowsIDs = [];
    rows.forEach(row => {
      if(!uniqueRowsIDs.includes(row.userID)){
        uniqueRows.push(row);
        uniqueRowsIDs.push(row.userID);
      }
    });
    return uniqueRows;
  }

  return (
    <Box sx={{
      height: "60vh",
      width: '100%',
      '& .MuiDataGrid-row': {
        borderBottom: '2px solid #f2f2f2',
      },
    }}
    >
      <DataGridPremium
        sx={{ borderRadius: "0 0 1em 1em", backgroundColor: 'white', padding: '0 1em', '& .MuiBadge-badge': { display: 'none' }, }}
        headerHeight={40}
        rowHeight={36}
        rows={deleteDuplicatedRows(data || [])}
        loading={isLoading || isFetching}
        getRowId={(row) => row.fullName}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
        }}
        pageSizeOptions={[5]}
        disableRowSelectionOnClick
      />
    </Box>
  );
}